import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Button, Tag, TagGroup } from "rsuite";
import { ReactComponent as IconClose } from "@assets/files/icons/general/close.svg";
import { Tag as TagInterface } from "@shared/interfaces";

import "./index.scss";

export type TaggedInputPickerProps = {
  tags: TagInterface[];
  onTagRemove: (tag: TagInterface["value"]) => void;
  onClearAll?: () => void;
  maxLines?: number;
};

const ELEMENT_HEIGHT = 32;
const ELEMENT_GAP = 10;

export const Tags: FC<TaggedInputPickerProps> = (props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tagGroupRef = useRef<HTMLDivElement>(null);

  const [showMore, setShowMore] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [moreElements, setMoreElements] = useState(0);

  const { tags, onTagRemove, onClearAll, maxLines = 4 } = props;
  const maxHeight = maxLines * ELEMENT_HEIGHT + (maxLines - 1) * ELEMENT_GAP;

  const setMaxHeight = useCallback(() => {
    if (!wrapperRef.current) return;

    wrapperRef.current.style.maxHeight = `${maxHeight}px`;
  }, [maxHeight]);

  useEffect(() => setMaxHeight(), [setMaxHeight]);

  useEffect(() => {
    if (!tagGroupRef.current) return;

    setShowMore(tagGroupRef.current.offsetHeight > maxHeight);

    const elements = tagGroupRef.current.querySelectorAll(".rs-tag-default");

    const { length } = [...elements].filter(
      (el) => (el as HTMLElement).offsetTop > (ELEMENT_HEIGHT + ELEMENT_GAP) * (maxLines - 1) + ELEMENT_GAP / 2,
    );
    setMoreElements(length);
  }, [tags, maxHeight, maxLines]);

  const onClearAllAction = () => {
    onClearAll?.();
    setShowMore(false);
    setShowAll(false);
    setMoreElements(0);
    setMaxHeight();
  };

  const showAllAction = () => {
    if (!wrapperRef.current) return;

    wrapperRef.current.style.maxHeight = "none";
    setShowAll(true);
  };

  return (
    <div className="tags-wrapper">
      <div ref={wrapperRef} className="tags-wrapper-overflow">
        <TagGroup ref={tagGroupRef}>
          {tags.map((tag, idx) => (
            <Tag key={idx} closable className="preset-tags" onClose={() => onTagRemove(tag.value)}>
              {tag.name}
            </Tag>
          ))}
          {tags.length > 0 && onClearAll && (
            <Button appearance="link" onClick={onClearAllAction}>
              <span>Clear All</span>
              <IconClose fill="#1e57c7" />
            </Button>
          )}
        </TagGroup>
      </div>
      {showMore && !showAll && (
        <div className="tags-wrapper-show-more-button-wrapper">
          {!!moreElements && (
            <Button appearance="link" className="show-more" onClick={showAllAction}>
              <span>Show {moreElements} more</span>
            </Button>
          )}
          {onClearAll && (
            <Button appearance="link" onClick={onClearAllAction}>
              <span>Clear All</span>
              <IconClose fill="#1e57c7" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
